import { Vehicle } from '../../types/vehicle';
import type { ApiClient } from './client';

export const vehiclesMethods = {
async getVehicles(): Promise<Vehicle[]> {
      try {
        const response = await this.makeRequest<Vehicle[]>('/vehicles.php');
        return response.data || [];
      } catch (error: any) {
        console.error('Failed to fetch vehicles:', error);
        throw new Error(error.message || 'Could not load vehicles.');
      }
    },


    async getVehicleFieldSuggestions(
      field: 'chassis' | 'engine' | 'transmission',
      q = '',
      limit = 10
    ): Promise<string[]> {
      // Public read-only: distinct values from is_public builds (Discover + Add Vehicle).
      const params = new URLSearchParams({
        field,
        q,
        limit: String(limit),
      });
      const response = await this.makeRequest<string[]>(
        `/vehicle_field_suggestions.php?${params.toString()}`,
        { method: 'GET' }
      );
      if (response.success && Array.isArray(response.data)) {
        return response.data;
      }
      throw new Error(response.error || response.message || 'Failed to load suggestions');
    },

    async getUserVehicles(): Promise<Vehicle[]> {
      try {
        if (!this.isAuthenticated()) {
          throw new Error('Authentication required');
        }

        const response = await this.makeRequest<Vehicle[]>('/vehicles_list.php', {
          method: 'GET'
        });

        if (response.success && Array.isArray(response.data)) {
          return response.data;
        } else if (response.success) {
          // Handle case where data is not an array, maybe an empty object
          return [];
        } else {
          throw new Error(response.message || 'Failed to fetch user vehicles');
        }
      } catch (error: any) {
        console.error('Failed to fetch user vehicles:', error);
        throw error;
      }
    },


    async createVehicle(vehicleData: {
      year: number;
      make: string;
      model: string;
      trim: string;
      color?: string;
      chassis?: string;
      engine?: string;
      transmission?: string;
      drivetrain?: string;
      build_status?: 'planned' | 'in_progress' | 'complete';
      exterior_finish_type?: 'paint' | 'wrap' | 'ppf';
      exterior_finish_color?: string;
      exterior_finish_brand?: string;
      exterior_finish_style?: string;
      nickname?: string;
      description?: string;
      is_primary?: boolean;
      is_public?: boolean;
      horsepower?: number;
      horsepower_measurement?: 'wheel' | 'crank' | null;
      torque?: number;
      redline?: number;
    }): Promise<Vehicle> {
      try {
        if (!this.isAuthenticated()) {
          throw new Error('Authentication required');
        }

        const response = await this.makeRequest<Vehicle>('/vehicles_create.php', {
          method: 'POST',
          body: JSON.stringify(vehicleData),
        });

        if (response.success && response.data) {
          return response.data;
        } else {
          throw new Error(response.message || 'Failed to create vehicle');
        }
      } catch (error: any) {
        console.error('Create vehicle error:', error);
        throw error;
      }
    },


    async updateVehicle(vehicleId: number, vehicleData: {
      year?: number;
      make?: string;
      model?: string;
      trim?: string;
      color?: string;
      chassis?: string;
      engine?: string;
      transmission?: string;
      drivetrain?: string;
      build_status?: 'planned' | 'in_progress' | 'complete';
      exterior_finish_type?: 'paint' | 'wrap' | 'ppf';
      exterior_finish_color?: string;
      exterior_finish_brand?: string;
      exterior_finish_style?: string;
      nickname?: string;
      description?: string;
      is_primary?: boolean;
      is_public?: boolean;
      /** Pass empty string or null to clear peak figures (backend coerces non-numeric to null). */
      horsepower?: number | string | null;
      horsepower_measurement?: 'wheel' | 'crank' | null | '';
      torque?: number | string | null;
      redline?: number | string | null;
      public_slug?: string;
    }): Promise<Vehicle> {
      try {
        if (!this.isAuthenticated()) {
          throw new Error('Authentication required');
        }

        const response = await this.makeRequest<Vehicle>(`/vehicles_update.php?id=${vehicleId}`, {
          method: 'PUT',
          body: JSON.stringify(vehicleData),
        });

        if (response.success && response.data) {
          return response.data;
        } else {
          throw new Error(
            (response as { error?: string }).error ||
              response.message ||
              'Failed to update vehicle'
          );
        }
      } catch (error: any) {
        console.error('Update vehicle error:', error);
        throw error;
      }
    },


    async deleteVehicle(vehicleId: number): Promise<void> {
      try {
        if (!this.isAuthenticated()) {
          throw new Error('Authentication required');
        }
        const response = await this.makeRequest<{}>(`/vehicles_delete.php?id=${vehicleId}`, {
          method: 'DELETE',
        });
        if (!response.success) {
          throw new Error(response.message || 'Failed to delete vehicle');
        }
      } catch (error: any) {
        console.error('Delete vehicle error:', error);
        throw error;
      }
    },

    async duplicateVehicle(
      vehicleId: number,
      opts?: { nickname?: string; public_slug?: string }
    ): Promise<Vehicle> {
      if (!this.isAuthenticated()) {
        throw new Error('Authentication required');
      }
      const response = await this.makeRequest<Vehicle>('/vehicles_duplicate.php', {
        method: 'POST',
        body: JSON.stringify({
          vehicle_id: vehicleId,
          nickname: opts?.nickname,
          public_slug: opts?.public_slug,
        }),
      });
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(
        (response as { error?: string }).error ||
          response.message ||
          'Could not duplicate this build'
      );
    },


    async getVehicle(vehicleId: number): Promise<Vehicle> {
      try {
        const response = await this.makeRequest<Vehicle>(`/vehicle_detail.php?id=${vehicleId}`);

        if (response.success && response.data) {
          return response.data;
        } else {
          throw new Error(response.message || `Vehicle with ID ${vehicleId} not found`);
        }
      } catch (error: any) {
        console.error(`Get vehicle by ID ${vehicleId} error:`, error);
        throw error;
      }
    },


    async getVehicleBySlug(username: string, vehicleSlug: string): Promise<Vehicle> {
      try {
        const response = await this.makeRequest<any>(`/vehicle_by_slug.php?username=${encodeURIComponent(username)}&slug=${encodeURIComponent(vehicleSlug)}`);

        if (response.success && response.data) {
          if (response.data.vehicle) {
            const vehicleData = response.data.vehicle;
            if (response.data.performance) {
              vehicleData.horsepower = response.data.performance.horsepower;
              vehicleData.horsepower_measurement =
                response.data.performance.horsepower_measurement ?? null;
              vehicleData.torque = response.data.performance.torque;
              vehicleData.redline = response.data.performance.redline;
            }
            return vehicleData;
          }
          // Fallback: merge performance if present
          if (response.data && response.data.performance) {
            return {
              ...response.data,
              horsepower: response.data.performance.horsepower,
              horsepower_measurement:
                response.data.performance.horsepower_measurement ?? null,
              torque: response.data.performance.torque,
              redline: response.data.performance.redline,
            };
          }
          return response.data;
        } else {
          throw new Error(response.message || 'Vehicle not found');
        }
      } catch (error: any) {
        console.error('Get vehicle by slug error:', error);
        throw error;
      }
    },

    // Vehicle like/unlike functionality
    async toggleVehicleLike(vehicleId: number, liked?: boolean): Promise<{
      success: boolean;
      action: 'liked' | 'unliked';
      liked: boolean;
      like_count: number;
      message: string;
    }> {
      try {
        if (!this.isAuthenticated()) {
          throw new Error('Authentication required to like vehicles');
        }

        const response = await this.makeRequest<{
          action: 'liked' | 'unliked';
          liked: boolean;
          like_count: number;
          message: string;
        }>('/vehicles_like.php', {
          method: 'POST',
          body: JSON.stringify({ vehicle_id: vehicleId, ...(liked === undefined ? {} : { liked }) })
        });

        if (response.success && response.data) {
          return {
            success: true,
            ...response.data
          };
        } else {
          throw new Error(response.error || 'Failed to toggle vehicle like');
        }
      } catch (error: any) {
        console.error('Error toggling vehicle like:', error);
        throw new Error(error.message || 'Failed to toggle vehicle like. Please try again.');
      }
    }
} satisfies ThisType<ApiClient>;
